import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { installationDateFormField } from 'types/assetModel/commonFields'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentSpace } from 'types/geometric/geckoComponentSpace'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { parseDate } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  requiredCoordinatesArraySchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoPolygonalPlateJSON {
  type: GeckoFeatureModelType.PolygonalPlate
  name: string
  feature_geometry: {
    coordinates: { x: number; y: number }[]
  }
  surface_specs?: {
    wall_nominal_thickness: number | undefined
    coating_nominal_thickness: number | undefined
  }
  installation_date: string | undefined
}

export const NewGeckoPolygonalPlateJSON: GeckoPolygonalPlateJSON = {
  type: GeckoFeatureModelType.PolygonalPlate,
  name: 'Plate',
  feature_geometry: {
    coordinates: [
      { x: Number.NaN, y: Number.NaN },
      { x: Number.NaN, y: Number.NaN },
      { x: Number.NaN, y: Number.NaN },
    ],
  },
  surface_specs: {
    wall_nominal_thickness: undefined,
    coating_nominal_thickness: undefined,
  },
  installation_date: undefined,
}

export class GeckoPolygonalPlate extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.PolygonalPlate
  readonly geometry: {
    readonly x: GeckoMeasurement
    readonly y: GeckoMeasurement
  }[]
  readonly polygonUnits: GeckoComponentSpace
  readonly wallNominalThickness: GeckoMeasurement | undefined
  readonly coatingNominalThickness: GeckoMeasurement | undefined
  readonly installationDate: Date | undefined

  constructor(
    id: string,
    name: string,
    geometry: { x: GeckoMeasurement; y: GeckoMeasurement }[],
    polygonUnits: GeckoComponentSpace,
    wallNominal: GeckoMeasurement | undefined,
    coatingNominal: GeckoMeasurement | undefined,
    installationDate: Date | undefined
  ) {
    super(id)
    this.name = name
    if (geometry.length < 3) {
      throw new Error(
        'Polygonal plates must have at least 3 points in their geometry'
      )
    }
    this.geometry = geometry
    this.polygonUnits = polygonUnits
    this.wallNominalThickness = wallNominal
    this.coatingNominalThickness = coatingNominal
    this.installationDate = installationDate
  }

  public static CreateFromJSON(
    componentID: string,
    plateID: string,
    plateJSON: GeckoPolygonalPlateJSON
  ): GeckoPolygonalPlate {
    const polygonUnits = GeckoComponentSpace.CreateFromMeasurementUnits(
      componentID,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )
    const thicknessMeasurementUnits = GeckoMeasurementUnit.Meters

    const featureGeometryData = {
      coordinates: plateJSON.feature_geometry.coordinates.map((coord) => ({
        x: GeckoMeasurement.CreateFromValue(coord.x, polygonUnits.x.unit),
        y: GeckoMeasurement.CreateFromValue(coord.y, polygonUnits.y.unit),
      })),
    }

    const surfaceSpecs = plateJSON.surface_specs as Record<string, number>

    const wallNominalThickness =
      surfaceSpecs && surfaceSpecs.wall_nominal_thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.wall_nominal_thickness as number,
            thicknessMeasurementUnits
          )
        : undefined
    const coatingNominalThickness =
      surfaceSpecs && surfaceSpecs.coating_nominal_thickness !== undefined
        ? GeckoMeasurement.CreateFromValue(
            surfaceSpecs.coating_nominal_thickness as number,
            thicknessMeasurementUnits
          )
        : undefined

    const installationDate =
      plateJSON.installation_date !== undefined
        ? parseDate(plateJSON.installation_date)
        : undefined
    const plateName = plateJSON.name as string

    return new GeckoPolygonalPlate(
      plateID,
      plateName,
      featureGeometryData.coordinates,
      polygonUnits,
      wallNominalThickness,
      coatingNominalThickness,
      installationDate
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoPolygonalPlateJSON>
  ): Map<string, GeckoPolygonalPlate> {
    return Object.keys(recordsJSON).reduce((resultObj, plateID) => {
      resultObj.set(
        plateID,
        GeckoPolygonalPlate.CreateFromJSON(
          componentID,
          plateID,
          recordsJSON[plateID]
        )
      )
      return resultObj
    }, new Map<string, GeckoPolygonalPlate>())
  }

  asJSON() {
    return generatePolygonalPlate(
      this.name,
      this.geometry,
      this.wallNominalThickness,
      this.coatingNominalThickness,
      this.installationDate
    )
  }
}

export function generatePolygonalPlate(
  name: string,
  geometry: { x: GeckoMeasurement; y: GeckoMeasurement }[],
  wallNominalThickness?: GeckoMeasurement,
  coatingNominalThickness?: GeckoMeasurement,
  installationDate?: Date
): GeckoPolygonalPlateJSON {
  const polygonalPlateJSON: GeckoPolygonalPlateJSON = {
    type: GeckoFeatureModelType.PolygonalPlate,
    name: name,
    installation_date: installationDate
      ? installationDate.toISOString().split('T')[0]
      : undefined,
    feature_geometry: {
      coordinates: geometry.map((coord) => ({
        x: coord.x.toNumber(GeckoMeasurementUnit.Meters),
        y: coord.y.toNumber(GeckoMeasurementUnit.Meters),
      })),
    },
    surface_specs: {
      wall_nominal_thickness:
        wallNominalThickness !== undefined
          ? wallNominalThickness.num
          : undefined,

      coating_nominal_thickness:
        coatingNominalThickness !== undefined
          ? coatingNominalThickness.num
          : undefined,
    },
  }

  return polygonalPlateJSON
}

export const PolygonalPlateFormFields = (
  polygonalPlateJSON: GeckoPolygonalPlateJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Plate Name',
      value: polygonalPlateJSON.name,
      unit_magnitude: null,
      initialValue: 'Plate',
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(polygonalPlateJSON.installation_date, false),
    {
      dataKey: 'coordinates',
      label: 'Coordinates',
      value: polygonalPlateJSON.feature_geometry.coordinates
        .map((coordinate) => `[${coordinate.x}, ${coordinate.y}]`)
        .join(', '),
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredCoordinatesArraySchema(3),
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Coordinates,
    },
    {
      dataKey: 'wall_nominal_thickness',
      label: 'Wall Nominal Thickness',
      value: polygonalPlateJSON.surface_specs?.wall_nominal_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'coating_nominal_thickness',
      label: 'Coating Nominal Thickness',
      value: polygonalPlateJSON.surface_specs?.coating_nominal_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export function parseCoordinatesFromString(
  coordinateString: string | undefined
): { x: number; y: number }[] {
  if (!coordinateString) {
    return []
  }

  return coordinateString
    .split('], [')
    .map((coord) => coord.replace(/[[\]]/g, ''))
    .map((coord) => {
      const [x, y] = coord.split(', ').map(Number)
      return { x, y }
    })
}

export const PolygonalPlateJSONFromFormFields = (formFields: FormField[]) => {
  const coordinates = getFormFieldByDataKey(formFields, 'coordinates')
    ?.value as string
  const wallNominalThickness = getFormFieldByDataKey(
    formFields,
    'wall_nominal_thickness'
  )
  const coatingNominalThickness = getFormFieldByDataKey(
    formFields,
    'coating_nominal_thickness'
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  return generatePolygonalPlate(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    parseCoordinatesFromString(coordinates).map(({ x, y }) => ({
      x: GeckoMeasurement.CreateFromValue(x, GeckoMeasurementUnit.Meters),
      y: GeckoMeasurement.CreateFromValue(y, GeckoMeasurementUnit.Meters),
    })),
    wallNominalThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          wallNominalThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    coatingNominalThickness?.value !== undefined
      ? GeckoMeasurement.CreateFromValue(
          coatingNominalThickness?.value as number,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    installationDate?.value !== undefined
      ? new Date(installationDate?.value as string)
      : undefined
  )
}

export const PolygonalPlateFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.PolygonalPlate,
  title: 'Polygonal Plates',
  singularName: 'Polygonal Plate',
  fields: ['name', 'coordinates'],
  tableMessage:
    'To see how to create a Polygonal Plate, please see the <a href="https://geckorobotics.atlassian.net/wiki/spaces/SE/pages/4595744769/Polygonal+Plates" target="_blank">documentation</a>.',
}
